import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Archive, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["audit_days", "Audit events", "days (terms acceptances are kept permanently)"],
  ["chat_days", "Chat logs", "days"],
  ["reminder_days", "Reminders", "days"],
];

export default function RetentionSettings({ onChanged }) {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/retention").then((r) => { setS(r.data); setForm({ audit_days: r.data.audit_days, chat_days: r.data.chat_days, reminder_days: r.data.reminder_days, auto_purge: !!r.data.auto_purge }); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/retention", { ...Object.fromEntries(FIELDS.map(([k]) => [k, Number(form[k])])), auto_purge: !!form.auto_purge });
      setS(data); toast.success("Retention policy saved"); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const purge = async () => {
    if (!window.confirm("Purge now? Records older than the retention windows will be permanently deleted (terms acceptances are always kept).")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/retention/purge");
      toast.success(`Purged ${data.audit_deleted} audit events, ${data.chats_deleted} chats, ${data.reminders_deleted} reminders`);
      load(); onChanged?.();
    } catch { toast.error("Purge failed"); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  return (
    <Card data-testid="retention-settings" className="p-5 bg-white border-slate-200 mb-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-display font-bold text-[#0A2A5E]"><Archive className="w-4 h-4 text-[#C7761A]" /> Retention policy</div>
          <p className="text-xs text-slate-500 mt-1">{form.auto_purge ? "Records older than these windows are purged automatically every 6 hours." : "Automatic purge is off — records are removed only when the workspace owner clicks Purge now."} Last run: {s.last_run ? `${new Date(s.last_run.ran_at).toLocaleString()} · ${s.last_run.audit_deleted + s.last_run.chats_deleted + s.last_run.reminders_deleted} record(s) removed` : "never"}.</p>
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input data-testid="retention-auto-purge" type="checkbox" checked={!!form.auto_purge} disabled={s.can_manage === false} onChange={(e) => setForm({ ...form, auto_purge: e.target.checked })} className="accent-[#0A2A5E]" />
            Enable automatic scheduled purge (workspace owner only)
          </label>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="retention-purge-btn" onClick={purge} disabled={busy || s.can_manage === false} title={s.can_manage === false ? "Restricted to the workspace owner" : undefined} className="border-rose-300 text-rose-700 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Purge now</Button>
          <Button size="sm" data-testid="retention-save-btn" onClick={save} disabled={busy || s.can_manage === false} title={s.can_manage === false ? "Restricted to the workspace owner" : undefined} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save policy"}</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {FIELDS.map(([k, label, hint]) => (
          <div key={k}>
            <Label className="text-xs">{label}</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input data-testid={`retention-${k}`} type="number" min={7} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-24 h-9" />
              <span className="text-[11px] text-slate-500">{hint}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
